import Cookies from 'js-cookie'
import { Fragment, useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { Modal, Spinner, Dropdown } from 'react-bootstrap'
import Swal from 'sweetalert2'
import config from '../../../../config'
import { ORDER_TYPE, PAYMENT_ACTION_STATUS, PAYMENT_METHOD, PAYMENT_STATUS } from '../../constants'
import { approveSlip, approveWithdraw, cancelPayment, recheckSlip, rejectSlip } from '../../utils/services'
import { Button, FormControl, GridContainer, StatusText, TextRight } from '../../styles'
import {
  canCancelPayment,
  canWithdrawPayment,
  getBackgroundColorByStatus,
  getTextColorByStatus,
  isDateValid,
  isHoldItemExpiredDisplay,
} from '../../utils'
import Slip from '../Slip'
import OrderDetailModal from '../OrderDetailModal'
import UserModal from '../../../User/UserModal'
import { useDispatch } from 'react-redux'
import { getPublicUrl, getUploadImageUrl } from '../../../../helpers/url'
import { getQRCodeData } from '../../../../helpers/qr-reader'
import { SlipContainer, StyledDropdownButton, SectionTitleContainer } from './index.styles'
import { sendMessageWs } from '../../utils/websocket'

const PaymentDetailModal = ({
  payment,
  setPayment,
  onHide,
  onCancelPayment,
  onApproveSlip,
  onRejectSlip,
  onApproveWithdraw,
  isReadOnly = false,
  webSocketRef,
}) => {
  const dispatch = useDispatch()
  const inputImageRef = useRef(null)

  const [isLoading, setIsLoading] = useState(false)
  const [order, setOrder] = useState(null)
  const [isShowOrderModal, setIsShowOrderModal] = useState(false)
  const [user, setUser] = useState(null)
  const [isShowUserModal, setIsShowUserModal] = useState(false)
  const [slips, setSlips] = useState([])
  const [isSlipLoading, setIsSlipLoading] = useState(false)
  const [note, setNote] = useState('')

  const canBlockPayment = payment?.action?.status !== PAYMENT_ACTION_STATUS.BLOCKED && !payment?.isDelivery
  const isLocked = payment?.action?.status !== PAYMENT_ACTION_STATUS.NONE

  const notes = useMemo(
    () => payment?.note?.sort((a, b) => new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime()),
    [payment?.note],
  )

  const isCanCancelHold = useMemo(() => {
    return (
      payment?.qrCode &&
      payment?.status === PAYMENT_STATUS.CANCEL &&
      isDateValid(payment?.holdItemExpiredAt) &&
      !payment?.refundItemAt
    )
  }, [payment?.holdItemExpiredAt, payment?.qrCode, payment?.refundItemAt, payment?.status])

  const debounceTimer = useRef(null)
  const debounce = useCallback((fn, timeout) => {
    clearTimeout(debounceTimer.current)
    debounceTimer.current = setTimeout(() => {
      fn()
    }, timeout)
  }, [])

  const onCreateDelivery = (data) => {
    setIsShowOrderModal(false)
    setPayment(data)
  }

  const getPaymentById = useCallback(
    async (id) => {
      try {
        const headers = new Headers()
        headers.append('Authorization', `Bearer ${Cookies.get('accessToken')}`)
        const response = await fetch(`${config.apiAdminGateway}/api/v2/admin/payments/${id}`, { headers }).then((res) =>
          res.json(),
        )

        if (response.status.code !== 0) throw new Error(response.status.description)

        setPayment(response.data)
      } catch (error) {
        console.error(error)
      } finally {
        setIsLoading(false)
      }
    },
    [setPayment],
  )

  const fetchSlips = useCallback(async (paymentId) => {
    setIsSlipLoading(true)
    try {
      const response = await fetch(`${config.apiAdminGateway}/api/v2/admin/payments/${paymentId}/pop`, {
        headers: {
          Authorization: `Bearer ${Cookies.get('accessToken')}`,
        },
      }).then((res) => res.json())

      if (response.status.code !== 0) throw new Error(response.status.description)
      setSlips(response.data)
    } catch (error) {
      console.error(error)
    } finally {
      setIsSlipLoading(false)
    }
  }, [])

  const handleClickAddNote = useCallback(async () => {
    if (!note) {
      return Swal.fire({
        icon: 'error',
        title: 'กรุณากรอกข้อความ',
      })
    }

    Swal.fire({
      title: `กรุณายืนยันการบันทึกโน้ตของหมายเลขคำสั่งซื้อ ${payment?.orderNo}`,
      text: `ข้อความที่บันทึก : ${note}`,
      icon: 'warning',
      showCancelButton: true,
      confirmButtonText: 'ยืนยัน',
      cancelButtonText: 'ยกเลิก',
    }).then(async (result) => {
      if (!result.isConfirmed) return

      try {
        const response = await fetch(`${config.apiAdminGateway}/api/v2/admin/payments/${payment?.id}/note`, {
          method: 'POST',
          body: JSON.stringify({ text: note }),
          headers: {
            Authorization: `Bearer ${Cookies.get('accessToken')}`,
          },
        }).then((res) => res.json())

        if (response.status.code !== 0) throw new Error(response.status.description)

        getPaymentById(payment?.id)
      } catch (error) {
        console.error(error)
      } finally {
        setNote('')
      }
    })
  }, [getPaymentById, note, payment])

  const handleAttachSlip = useCallback(
    async (imageUrl = '', qrText = '', paymentMethod = PAYMENT_METHOD.CASH) => {
      const body = paymentMethod === PAYMENT_METHOD.POINT ? { paymentMethod } : { imageUrl, qrText, paymentMethod }
      try {
        const response = await fetch(`${config.apiAdminGateway}/api/v2/admin/payments/${payment.id}/attach`, {
          method: 'POST',
          body: JSON.stringify(body),
          headers: {
            Authorization: `Bearer ${Cookies.get('accessToken')}`,
          },
        }).then((res) => res.json())

        if (response.status.code !== 0) throw new Error(response.status.description)

        getPaymentById(payment?.id)
      } catch (error) {
        console.error(error)
        Swal.fire({
          icon: 'error',
          title: 'ไม่สามารถแนบสลิปได้',
          text: error.message,
        })
      }
    },
    [getPaymentById, payment?.id],
  )

  const handleUploadSlip = async (event) => {
    const file = event.target.files[0]
    const fileType = ['jpeg', 'jpg'].includes(file.type.split('/')[1]) ? 'JPEG' : 'PNG'

    if (!file) {
      inputImageRef.current.value = null
      return
    }

    try {
      dispatch({ type: 'loading_show' })

      const url = await getUploadImageUrl(fileType)
      const imageUrl = getPublicUrl(url)

      const response = await fetch(url, {
        method: 'PUT',
        body: file,
      })

      if (!response.ok) throw new Error('Error uploading image')

      const objectURL = URL.createObjectURL(file)
      const qr = await getQRCodeData(objectURL)

      if (!qr) {
        return Swal.fire({
          title: 'ไม่พบข้อมูล QR Code',
          text: 'ต้องการแนบสลิปที่ไม่มี QR Code หรือไม่',
          showCancelButton: true,
          confirmButtonText: 'ยืนยัน',
          cancelButtonText: 'ยกเลิก',
          imageUrl: imageUrl,
        }).then(async (result) => {
          if (result.isConfirmed) {
            await handleAttachSlip(imageUrl, '')
          }
        })
      }

      await handleAttachSlip(imageUrl, qr)
    } catch (error) {
      console.error(error)

      Swal.fire({
        title: 'ไม่สามารถเพิ่มรูปภาพได้',
        html: 'กรุณาตรวจสอบขนาดไฟล์และ<br>นามสกุลไฟล์ที่รองรับ',
        icon: 'error',
      })
    } finally {
      dispatch({ type: 'loading_hide' })
      inputImageRef.current.value = null
    }
  }

  const handleClickUsePoint = useCallback(async () => {
    Swal.fire({
      title: 'ชำระโดยนกแคช',
      text: 'ยืนยันการชำระโดยใช้นกแคชของลูกค้า',
      showCancelButton: true,
      confirmButtonText: 'ยืนยัน',
      cancelButtonText: 'ยกเลิก',
    }).then(async (result) => {
      if (!result.isConfirmed) return
      await handleAttachSlip('', '', PAYMENT_METHOD.POINT)
    })
  }, [handleAttachSlip])

  const handleOnClickSendLineMessage = async (userId) => {
    try {
      const fullName = `ชื่อ-นามสกุล: ${user.firstName} ${user.lastName} (${user.name})`

      Swal.fire({
        title: `<div><span>ส่งข้อความไลน์</span><div style="font-size: 14px; font-weight: normal; margin-top: 4px; text-align: left;">${fullName}</div></div>`,
        input: 'textarea',
        inputPlaceholder: 'กรอกข้อความที่ต้องการส่ง',
        showCancelButton: true,
        confirmButtonText: 'ส่ง',
        cancelButtonText: 'ยกเลิก',
        preConfirm: async (message) => {
          if (!message) {
            Swal.showValidationMessage('กรุณากรอกข้อความ')
            return
          }

          await fetch(`${config.apiAdminGateway}/api/v2/admin/notification/line`, {
            method: 'POST',
            headers: {
              Authorization: `Bearer ${Cookies.get('accessToken')}`,
            },
            body: JSON.stringify({
              userId: userId,
              message: message,
            }),
          })
            .then((res) => res.json())
            .then((res) => {
              if (res.status.code !== 0) throw new Error(res.status.description)

              Swal.fire({
                icon: 'success',
                title: 'ส่งข้อความสำเร็จ',
                showConfirmButton: true,
              })
            })
            .catch((err) => {
              Swal.fire({
                icon: 'error',
                title: 'ส่งข้อความไม่สำเร็จ',
                text: err.message,
                showConfirmButton: true,
              })
            })
        },
      })
    } catch (error) {
      console.error(error)
    }
  }

  const handleClickCancelPayment = useCallback(
    async (id) => {
      try {
        await cancelPayment(id).then((isConfirmed) => {
          isConfirmed && onCancelPayment?.()
        })
      } catch (error) {
        console.error(error)
      }
    },
    [onCancelPayment],
  )

  const handleClickApproveWithdraw = useCallback(
    async (id) => {
      try {
        await approveWithdraw(id).then((isConfirmed) => {
          if (!isConfirmed) return
          getPaymentById(payment?.id)
          onApproveWithdraw(payment?.id)
        })
      } catch (error) {
        console.error(error)
      }
    },
    [getPaymentById, onApproveWithdraw, payment?.id],
  )

  const handleClickApproveSlip = useCallback(
    async (id, refNumber, amount) => {
      if (!refNumber || !amount)
        return Swal.fire({
          icon: 'error',
          title: 'กรุณากรอกข้อมูลให้ครบถ้วน',
        })

      try {
        await approveSlip(id, refNumber, amount).then((isConfirmed) => {
          if (!isConfirmed) return
          getPaymentById(payment?.id)
          onApproveSlip?.()
        })
      } catch (error) {
        console.error(error)
      }
    },
    [getPaymentById, onApproveSlip, payment],
  )

  const handleClickRejectSlip = useCallback(
    async (id) => {
      try {
        await rejectSlip(id).then((isConfirmed) => {
          if (!isConfirmed) return
          getPaymentById(payment?.id)
          onRejectSlip?.()
        })
      } catch (error) {
        console.error(error)
      }
    },
    [getPaymentById, onRejectSlip, payment],
  )

  const handleClickRecheckSlip = useCallback(
    async (imageUrl, id, qrText) => {
      try {
        await recheckSlip(imageUrl, id, qrText).then((isConfirmed) => {
          if (!isConfirmed) return
          getPaymentById(payment?.id)
        })
      } catch (error) {
        console.error(error)
      }
    },
    [getPaymentById, payment],
  )

  const handleClickRefresh = (id) => {
    getPaymentById(id)
  }

  const handleClickCancelHold = useCallback(
    async (id) => {
      try {
        const result = await Swal.fire({
          title: 'ยืนยันการยกเลิก Hold ลอตเตอรี่',
          text: 'คุณต้องการยกเลิก Hold ลอตเตอรี่ นี้หรือไม่?',
          icon: 'warning',
          showCancelButton: true,
          confirmButtonText: 'ยืนยัน',
          cancelButtonText: 'ยกเลิก',
        })

        if (!result.isConfirmed) return

        const response = await fetch(`${config.apiAdminGateway}/api/v2/admin/payments/${id}/cancelhold`, {
          method: 'POST',
          headers: {
            Authorization: `Bearer ${Cookies.get('accessToken')}`,
          },
        }).then((res) => res.json())

        if (response.status.code !== 0) throw new Error(response.status.description)

        await Swal.fire({
          icon: 'success',
          title: 'ยกเลิก Hold ลอตเตอรี่ สำเร็จ',
        })

        getPaymentById(payment?.id)
      } catch (error) {
        console.error(error)
        Swal.fire({
          icon: 'error',
          title: 'ไม่สามารถยกเลิก Hold ลอตเตอรี่ ได้',
          text: error.message,
        })
      }
    },
    [getPaymentById, payment?.id],
  )

  const handleClickOrderNo = useCallback(
    async (id, orderType) => {
      if (order) {
        setIsShowOrderModal(true)
        return
      }

      try {
        const headers = new Headers()
        headers.append('Authorization', `Bearer ${Cookies.get('accessToken')}`)
        const url =
          orderType === ORDER_TYPE.COUPON_ORDER ? `/api/v1/admin/coupon-orders/${id}` : `/api/v3/admin/orders/${id}`
        const response = await fetch(`${config.apiAdminGateway}${url}`, { headers }).then((res) => res.json())

        if (response.status.code !== 0) throw new Error(response.status.description)

        setOrder(response.data)
        setIsShowOrderModal(true)
      } catch (error) {
        console.error(error)
      }
    },
    [order],
  )

  const fetchUserByUserId = useCallback(async (userId) => {
    try {
      const headers = new Headers()
      headers.append('Authorization', `Bearer ${Cookies.get('accessToken')}`)
      const response = await fetch(`${config.apiAdminGateway}/api/v1/admin/users/${userId}`, {
        headers,
      }).then((res) => res.json())

      if (response.status.code !== 0) throw new Error(response.status.description)

      const { data: user } = response

      setUser({
        ...user,
        birthDay: {
          year: new Date(user.birthDay).getFullYear(),
          month: new Date(user.birthDay).getMonth() + 1,
          day: new Date(user.birthDay).getDate(),
        },
        ownerBankName: user.bankName,
        ownerBankNo: user.bankNo,
      })
    } catch (error) {
      console.error(error)
    }
  }, [])

  const handleClickProblemButton = useCallback(
    (id) => {
      Swal.fire({
        title: 'ยืนยันการบันทึกว่ามีปัญหา?',
        showCancelButton: true,
        confirmButtonText: 'ยืนยัน',
        cancelButtonText: 'ยกเลิก',
      }).then(async (result) => {
        if (!result.isConfirmed) return

        onHide()

        sendMessageWs(webSocketRef.current, id, PAYMENT_ACTION_STATUS.BLOCKED)
      })
    },
    [onHide, webSocketRef],
  )

  const handleCloseModal = useCallback(() => {
    onHide?.()
    setOrder(null)
    setIsShowOrderModal(false)
    setUser(null)
    setIsShowUserModal(false)
    setNote('')
    setSlips([])
  }, [onHide])

  useEffect(() => {
    const getDetails = async (id) => {
      setIsLoading(true)
      try {
        await getPaymentById(id)
      } catch (error) {
        console.error(error)
      } finally {
        setIsLoading(false)
      }
    }

    if (payment?.id) {
      getDetails(payment?.id)
    }
  }, [getPaymentById, payment?.id])

  useEffect(() => {
    if (payment?.id) {
      fetchSlips(payment?.id)
    }
  }, [fetchSlips, payment])

  useEffect(() => {
    if (payment?.userId) {
      fetchUserByUserId(payment?.userId)
    }
  }, [fetchUserByUserId, payment?.userId])

  console.log('payment', payment)
  

  return (
    <Modal show={!!payment} onHide={handleCloseModal} size="lg" scrollable centered enforceFocus={false}>
      <Modal.Header style={{ backgroundColor: getBackgroundColorByStatus(payment?.status) }} closeButton>
        <Modal.Title className="text-center">
          <span>หมายเลขออเดอร์ #{payment?.orderNo}</span>
          {isLocked && <span className="pl-2">(ล็อคโดยแอดมิน: {payment?.action?.adminName})</span>}
        </Modal.Title>
      </Modal.Header>
      {isLoading ? (
        <Modal.Body style={{ padding: '16px', display: 'flex', justifyContent: 'center' }}>
          <Spinner animation="border" role="status">
            <span className="sr-only">Loading...</span>
          </Spinner>
        </Modal.Body>
      ) : (
        payment && (
          <Modal.Body style={{ padding: '16px', display: 'flex', flexDirection: 'column' }}>
            <div>
              <h4 className="text-center relative">
                ข้อมูลการชำระเงิน
                <span
                  style={{
                    position: 'absolute',
                    right: '16px',
                    top: '8px',
                    display: 'flex',
                    alignItems: 'flex-start',
                    gap: '4px',
                  }}
                >
                  {canBlockPayment && (
                    <button
                      className="btn btn-dark"
                      style={{ padding: '8px 16px' }}
                      onClick={() => handleClickProblemButton(payment.id)}
                    >
                      มีปัญหา
                    </button>
                  )}
                  <button
                    className="btn btn-primary"
                    onClick={() => debounce(() => handleClickRefresh(payment.id), 250)}
                    style={{ padding: '8px 16px' }}
                  >
                    <span className="fa fa-refresh" />
                  </button>
                </span>
              </h4>
              <GridContainer>
                <TextRight>หมายเลขออเดอร์ :</TextRight>
                <span>
                  {[ORDER_TYPE.LOTTERY, ORDER_TYPE.COUPON_ORDER].includes(payment?.orderType) && !payment?.orderRef ? (
                    <button
                      className="btn btn-primary"
                      style={{
                        width: 'fit-content',
                        padding: '0px 8px',
                      }}
                      onClick={() => debounce(() => handleClickOrderNo(payment.orderId, payment.orderType), 250)}
                    >
                      <span>{payment?.orderNo}</span>
                    </button>
                  ) : (
                    payment?.orderNo
                  )}
                </span>
              </GridContainer>
              {payment?.orderRef && (
                <GridContainer>
                  <TextRight>หมายเลขออเดอร์อ้างอิง :</TextRight>
                  {Number(payment.orderId) !== 0 ? (
                    <button
                      className="btn btn-primary"
                      style={{
                        width: 'fit-content',
                        padding: '0px 8px',
                      }}
                      onClick={() => debounce(() => handleClickOrderNo(payment.orderId), 250)}
                    >
                      {payment?.orderRef}
                    </button>
                  ) : (
                    <span>{payment?.orderRef}</span>
                  )}
                </GridContainer>
              )}
              <GridContainer>
                <TextRight>ข้อมูลลูกค้า :</TextRight>
                <div style={{ display: 'flex', gap: 4 }}>
                  <button
                    className="btn btn-primary light"
                    style={{
                      width: 'fit-content',
                      padding: '0px 8px',
                    }}
                    onClick={() => setIsShowUserModal(true)}
                  >
                    {user?.firstName} {user?.lastName} ({user?.name})
                  </button>
                  <button
                    className="btn btn-success light"
                    style={{
                      width: 'fit-content',
                      padding: '0px 8px',
                    }}
                    disabled={!payment?.userId}
                    onClick={() => handleOnClickSendLineMessage(payment?.userId)}
                  >
                    เปิดแชทไลน์
                  </button>
                </div>
              </GridContainer>
              <GridContainer>
                <TextRight>ประเภทออเดอร์ :</TextRight>
                <span>{payment.orderType}</span>
              </GridContainer>
              <GridContainer>
                <TextRight>สถานะ :</TextRight>
                <div style={{ display: 'flex', gap: '8px', alignItems: 'baseline' }}>
                  <StatusText color={getTextColorByStatus(payment.status)}>{payment.status}</StatusText>
                  {isReadOnly ? null : canWithdrawPayment(payment) ? (
                    <button
                      className="btn btn-primary"
                      style={{
                        padding: '4px 8px',
                      }}
                      onClick={() => handleClickApproveWithdraw(payment.id)}
                    >
                      อนุมัติการคืนเงิน
                    </button>
                  ) : (
                    <>
                      {canCancelPayment(payment) && (
                        <button
                          className="btn btn-danger"
                          style={{
                            padding: '4px 8px',
                          }}
                          onClick={() => handleClickCancelPayment(payment.id)}
                        >
                          ยกเลิกออเดอร์
                        </button>
                      )}
                      {isCanCancelHold && (
                        <button
                          className="btn btn-warning"
                          style={{
                            padding: '4px 8px',
                          }}
                          onClick={() => handleClickCancelHold(payment.id)}
                        >
                          ยกเลิก Hold ลอตเตอรี่
                        </button>
                      )}
                    </>
                  )}
                </div>
              </GridContainer>
              <GridContainer>
                <TextRight>ยอดรวม :</TextRight>
                <div>
                  <span style={{ color: 'red', fontWeight: 600, fontSize: '1.125rem' }}>
                    {Number(payment.total).toLocaleString('TH-th', { maximumFractionDigits: 2 })}
                  </span>
                  <span> บาท</span>
                </div>
              </GridContainer>
              {payment.remainingAmount > 0 && (
                <GridContainer>
                  <TextRight>ชำระแล้ว :</TextRight>
                  <div>
                    <span style={{ color: 'red', fontWeight: 600, fontSize: '1.125rem' }}>
                      {Number(payment.total - payment.remainingAmount).toLocaleString('TH-th', {
                        maximumFractionDigits: 2,
                      })}
                    </span>
                    <span> บาท</span>
                  </div>
                </GridContainer>
              )}
              <GridContainer>
                <TextRight>{payment?.remainingAmount < 0 ? 'ชำระเกิน' : 'ค้างชำระ'}: </TextRight>
                <div>
                  <span style={{ color: 'red', fontWeight: 600, fontSize: '1.125rem' }}>
                    {Number(
                      payment?.remainingAmount < 0 ? payment?.remainingAmount * -1 : payment?.remainingAmount,
                    ).toLocaleString('th-TH', { maximumFractionDigits: 2 })}
                  </span>
                  <span> บาท</span>
                </div>
              </GridContainer>
              <GridContainer>
                <TextRight>สร้างเมื่อ :</TextRight>
                <span>{new Date(payment?.createdAt).toLocaleString('th-TH')}</span>
              </GridContainer>
              {isHoldItemExpiredDisplay(payment) && (
                <GridContainer>
                  <TextRight>หมดอายุเมื่อ:</TextRight>
                  <span>{new Date(payment?.holdItemExpiredAt).toLocaleString('th-TH')}</span>
                </GridContainer>
              )}
              {!isReadOnly && (
                <GridContainer>
                  <TextRight>โน้ต :</TextRight>
                  <div
                    as="form"
                    style={{
                      display: 'flex',
                      gap: '8px',
                    }}
                  >
                    <FormControl value={note} onChange={(event) => setNote(event.target.value)} />
                    <Button
                      className="fa fa-plus"
                      isActive
                      color="lightblue"
                      disabled={!note}
                      onClick={(event) => {
                        event.preventDefault()
                        handleClickAddNote()
                      }}
                    />
                  </div>
                </GridContainer>
              )}
              <GridContainer>
                <TextRight>ประวัติโน้ต :</TextRight>
                <div>
                  {notes?.map((note, index) => {
                    const updateDate = new Date(note.createdAt).toLocaleString('TH-th')
                    return (
                      <div key={index} style={{ fontSize: '0.875rem' }}>
                        {note.text} ({updateDate} น.)
                      </div>
                    )
                  })}
                </div>
              </GridContainer>
            </div>
            <div>
              <hr />
            </div>
            <SectionTitleContainer>
              <h4 className="text-center">หลักฐานการชำระเงิน</h4>
              {/open|confirm/i.test(payment?.status) && (
                <Fragment>
                  <input
                    id="slip"
                    name="slip"
                    type="file"
                    ref={inputImageRef}
                    accept="image/jpg,image/jpeg,image/png"
                    style={{ display: 'none' }}
                    onChange={handleUploadSlip}
                  />
                  <StyledDropdownButton variant="primary" title="ชำระเพิ่ม" size="sm" flip>
                    <Dropdown.Item onClick={() => inputImageRef?.current?.click()}>แนบสลิป</Dropdown.Item>
                    <Dropdown.Item onClick={handleClickUsePoint}>ใช้นกแคช</Dropdown.Item>
                  </StyledDropdownButton>
                </Fragment>
              )}
            </SectionTitleContainer>
            {isSlipLoading ? (
              <div className="mx-auto">
                <Spinner animation="border" role="status">
                  <span className="sr-only">Loading...</span>
                </Spinner>
              </div>
            ) : (
              <SlipContainer>
                {slips?.length > 0 ? (
                  slips.map((slip, index) => (
                    <Fragment key={slip.id}>
                      <Slip
                        isReadOnly={isReadOnly}
                        slip={slip}
                        onClickApprove={handleClickApproveSlip}
                        onClickReject={handleClickRejectSlip}
                        onClickRecheck={handleClickRecheckSlip}
                      />
                    </Fragment>
                  ))
                ) : (
                  <h4 className="text-red text-center">ไม่พบข้อมูลสลิป</h4>
                )}
              </SlipContainer>
            )}
          </Modal.Body>
        )
      )}
      {isShowOrderModal && (
        <OrderDetailModal
          show={isShowOrderModal}
          order={order}
          paymentStatus={payment?.status}
          onHide={handleCloseModal}
          onCloseOrderModal={() => {
            setIsShowOrderModal(false)
            setOrder(null)
          }}
          onCancelPayment={onCancelPayment}
          onCreateDelivery={onCreateDelivery}
        />
      )}
      {user && (
        <UserModal
          userModalVisible={isShowUserModal}
          setUserModalVisible={setIsShowUserModal}
          selectedUser={user}
          setSelectedUser={setUser}
          onClose={() => {
            setIsShowUserModal(false)
            setUser(null)
          }}
        />
      )}
    </Modal>
  )
}

export default PaymentDetailModal
